import { createFileRoute, redirect, Outlet, useRouterState } from '@tanstack/react-router'
import { Suspense, LazyExoticComponent, ReactElement, JSXElementConstructor, lazy } from "react";

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/toast';
import { getToken } from "@/lib/token/token";
import { checkTermsStateAndRedirect, checkTwoFactorStateAndRedirect } from "@/lib/utils";
import { AppBreadcrumbs } from '@/modules/navigation/components/app-breadcrumbs';
import { AppSidebar } from '@/modules/navigation/components/app-sidebar';

type TanstackDevTools =
  | LazyExoticComponent<(props: any) => ReactElement<unknown, string | JSXElementConstructor<any>> | null>
  | (() => null)

let TanStackRouterDevtools: TanstackDevTools = () => null
let TanStackQueryDevtools: TanstackDevTools = () => null

if (process.env.NODE_ENV !== 'production') {
  TanStackRouterDevtools = lazy(() => import('@tanstack/react-router-devtools').then(res => ({
    default: res.TanStackRouterDevtools
  })))

  TanStackQueryDevtools = lazy(() => import('@tanstack/react-query-devtools').then(res => ({
    default: res.ReactQueryDevtools
  })))
}

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ location }) => {

    // If there's no token in the localStorage, go away to Login
    if (!getToken()) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      })
    }

    // Check if there's a 2FA token, and redirect only to 2FA
    checkTwoFactorStateAndRedirect()

    // Terms not accepted? The only page they get is /terms. Must stay after the 2FA check: an
    // interim 2FA token carries no terms claim and must never be evaluated here.
    checkTermsStateAndRedirect(location.href)
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const matches = useRouterState({ select: s => s.matches })

  // Important Note: The reason that this is [2] is because the _auth route group.
  // If you ever have an issue with breadcrumbs, you likely have gone outside the grouping used here.
  // Consider moving to Tanstack if we encounter issues where more route groups are necessary for breadcrumbs.
  const breadCrumbs = matches[2]?.context.breadcrumbs || []
  const breadCrumbsText = matches[2]?.context.children || null

  return (
    <>
      <SidebarProvider>
        <AppSidebar />

        <SidebarInset>
          <AppBreadcrumbs breadcrumbs={breadCrumbs}>
            {breadCrumbsText}
          </AppBreadcrumbs>

          <div className="flex flex-1 flex-col gap-4 sm:p-8">
            <Outlet />
          </div>

        </SidebarInset>
      </SidebarProvider>

      <Suspense>
        <TanStackQueryDevtools buttonPosition="bottom-right" initialIsOpen={false} />
        <TanStackRouterDevtools position="top-right" />
      </Suspense>

      <Toaster />
    </>
  )
}